import "./App.scss";
import { BrowserRouter } from "react-router-dom";
import Routing from "./components/routing/Routing";
import RootRouting from "./components/routing/RootRouting";
import Notification from "./components/custom-components/notification/Notification";
import { ThemeProvider } from "@mui/material";
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from "@emotion/react";

import { emotionCache } from "./emotionCache";
import { theme } from "./utils/theme";
import ProgressBar from "./components/modules/loading/Loader";
import useSessionCheck from "./components/custom-hooks/useSessionCheck";
import NavigationLoader from "./components/custom-components/navigation-loader/NavigationLoader";
// @ts-ignore
import { initColors, ORIGINAL_COLORS} from "ntc-ts";
import { AuthProvider } from "./context/AuthContext";
import { PermissionsProvider } from "./context/PermissionsContext";
import { BugReportWidget } from "./components/BugReportWidget";
import ErrorBoundary from "./components/custom-components/error-boundary/ErrorBoundary";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./services/queryClient";
import { useHostBrandedTab } from "./hooks/useHostBrandedTab";

function App() {
  useSessionCheck();
  // Brand the browser tab (title + favicon) from the page hostname, so a
  // tenant with a dedicated domain shows its own name/logo in the tab BOTH on
  // the login screen and inside the app. Neutral hosts keep the static default.
  useHostBrandedTab();
  initColors(ORIGINAL_COLORS);

  return (
    // CacheProvider is the OUTERMOST wrapper. It hands MUI a single, explicit
    // app-owned Emotion cache instead of letting MUI lazily create its own on
    // first styled-component render. That lazy creation was the init-time edge
    // from the MUI/Emotion runtime back into React that produced a chunk-cycle
    // TDZ crash when React/MUI were split into separate vendor chunks; owning
    // the cache here is the canonical break (see src/emotionCache.ts). It must
    // sit OUTSIDE ThemeProvider so the theme's styles insert through this cache.
    <CacheProvider value={emotionCache}>
    {/* QueryClientProvider provides data context and renders no UI, so its
        placement doesn't affect MUI theming. Defaults are tuned to match the
        app's prior non-refetching behavior (see services/queryClient.ts). */}
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <PermissionsProvider>

      <ThemeProvider theme={theme} noSsr defaultMode="system">

        <Notification />
        <CssBaseline />
        <ProgressBar />
        <BugReportWidget />

        <ErrorBoundary>
          <BrowserRouter basename={
            ((): string | undefined => {
              const p = window.location.pathname || "";
              return p === "/root" || p.startsWith("/root/") ? "/root" : undefined;
            })()
          }>
            <NavigationLoader />
            {window.location.pathname === "/root" || window.location.pathname.startsWith("/root/") ? (
              <RootRouting />
            ) : (
              <Routing />
            )}
          </BrowserRouter>
        </ErrorBoundary>
      </ThemeProvider>

      </PermissionsProvider>
    </AuthProvider>
    </QueryClientProvider>
    </CacheProvider>
  );
}

export default App;
